import React from 'react';
import { Button, Group, Icon, Dialog } from '@economic/taco';
import { t } from '../l10n/l10n';
import { Form } from '../Form';
import { FormProps } from '../../../forms/types';

type ConfirmationDialogProps = {
    label: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    onCancel: () => void;
    onConfirm: (args: any) => void;
    focusConfirmButton?: boolean;
    warning?: boolean;
    showNotifications?: boolean;
} & Pick<FormProps<any>, 'onSubmitted' | 'onSubmitError'>;

export default function ConfirmationDialog({
    label,
    message,
    confirmText,
    cancelText,
    onCancel,
    onConfirm,
    focusConfirmButton = false,
    warning = false,
    onSubmitted,
    onSubmitError: handleSubmitError,
    showNotifications = false,
}: ConfirmationDialogProps) {
    // Temporary solution for https://jira.visma.com/browse/EC-29868
    // For an edge case, when modal is shown, the focus remains on the table or element triggering
    // the visibility of the modal. Using autoFocus on buttons doesn't work.
    // Remove this ref logic when EC-29868 is fixed.
    const confirmButtonRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        if (focusConfirmButton) {
            confirmButtonRef.current?.focus();
        }
    }, [focusConfirmButton]);

    const handleConfirm = (response: any) => onConfirm(response);

    const handleSubmitted = (response: any) => {
        onSubmitted?.(response);
        onCancel();
    };

    return (
        <Form
            onSubmit={handleConfirm}
            onSubmitError={handleSubmitError}
            onSubmitted={handleSubmitted}
            skipNotifications={!showNotifications}>
            {({ submitting }) => (
                <>
                    <Dialog.Title>
                        {warning && <Icon name="warning" className="rounded-full yt-yellow-solid mr-4" />}
                        {label}
                    </Dialog.Title>
                    {message}

                    <Dialog.Footer>
                        <Group>
                            <Button onClick={onCancel}>{cancelText || t`BUTTON_CANCEL`}</Button>
                            <Button type="submit" disabled={submitting} appearance="primary" ref={confirmButtonRef}>
                                {confirmText || t`SHARED_FORM_OK`}
                            </Button>
                        </Group>
                    </Dialog.Footer>
                </>
            )}
        </Form>
    );
}
